
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* Video Modal */}
      {isVideoOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fadeIn"
          onClick={() => setIsVideoOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl mx-4 aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <iframe
              src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
              title="Your App Wizard Demo Video"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/aa6a560e27778e447c7f9998038c9bec.jpg"
          alt="Hero Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/10"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-48 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full text-white mb-8 border border-purple-400/30 shadow-lg shadow-purple-500/20 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <i className="ri-smartphone-line text-purple-400"></i>
              <span className="text-sm font-semibold">No Code Required</span>
            </div>

            <h1 className={`text-6xl font-extrabold text-white leading-tight mb-7 transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Build Your Dream App
              <br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">No Code Required</span>
            </h1>

            <p className={`text-xl text-white/95 leading-relaxed mb-12 transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Create stunning iOS and Android apps in minutes with our intuitive drag-and-drop builder. No programming skills needed — just your vision.
            </p>

            <div className={`flex items-center gap-5 transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Link
                to="/get-started"
                className="group px-9 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-xl shadow-purple-500/40 hover:shadow-2xl hover:shadow-purple-500/50 whitespace-nowrap cursor-pointer flex items-center gap-3 font-semibold text-sm hover:scale-105"
              >
                Start Building Free
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <button
                onClick={() => setIsVideoOpen(true)}
                className="px-9 py-4 bg-white/10 backdrop-blur-md text-white rounded-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm border border-white/30 flex items-center gap-2 hover:scale-105"
              >
                <i className="ri-play-circle-line text-lg"></i>
                Watch Demo
              </button>
            </div>

            {/* Trust Indicators */}
            <div className={`flex items-center gap-8 mt-12 transition-all duration-700 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-white/20"></div>
                  ))}
                </div>
                <span className="text-white/90 text-sm font-medium ml-2">10,000+ creators</span>
              </div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
                <span className="text-white/90 text-sm font-medium ml-1">4.9/5 rating</span>
              </div>
            </div>
          </div>

          {/* Right Content - Phone Mockup */}
          <div className={`relative flex justify-end transition-all duration-1000 delay-500 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="relative w-52">
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full opacity-20 blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>

              {/* Phone Mockup */}
              <div className="relative z-10 bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2.5rem] p-2 shadow-2xl border-4 border-gray-700">
                <div className="bg-white rounded-[2rem] overflow-hidden">
                  <div className="bg-gray-900 h-6 rounded-b-2xl mx-auto w-28 relative">
                    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-700 rounded-full"></div>
                  </div>
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20mobile%20app%20interface%20design%20showcasing%20beautiful%20ecommerce%20shopping%20application%20with%20clean%20minimalist%20layout%20featuring%20product%20grid%20colorful%20product%20cards%20smooth%20gradients%20elegant%20typography%20floating%20action%20buttons%20bottom%20navigation%20bar%20professional%20ui%20ux%20design%20in%20vibrant%20purple%20pink%20and%20white%20color%20scheme%20high%20quality%20app%20screenshot%20perfect%20for%20app%20builder%20platform%20demonstration&width=400&height=800&seq=phone-app-screen-ecommerce-v3-002&orientation=portrait"
                    alt="App Preview"
                    className="w-full h-96 object-cover object-top"
                  />
                </div>
              </div>

              {/* Floating Feature Card - left side */}
              <div className="absolute -left-40 top-1/4 bg-white/95 backdrop-blur-md rounded-xl p-3 shadow-xl border border-purple-200 animate-float">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <i className="ri-drag-drop-line text-white text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-medium">Drag &amp; Drop</div>
                    <div className="text-xs font-bold text-gray-900">Easy Builder</div>
                  </div>
                </div>
              </div>

              {/* Floating Feature Card - bottom left */}
              <div className="absolute -left-48 bottom-8 bg-white/95 backdrop-blur-md rounded-xl p-3 shadow-xl border border-pink-200 animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <i className="ri-rocket-line text-white text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-medium">Instant</div>
                    <div className="text-xs font-bold text-gray-900">Publish</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
